import React, { useState, useRef, useEffect } from "react";
import { Bot, Plus, Folder, LayoutDashboard, Users, X, MessagesSquare, LifeBuoy, Key, StickyNote, CalendarDays, Megaphone, Settings, LogOut, ChevronUp, PanelLeftClose, PanelLeft, Coins, BarChart3, Building2, FileBarChart, UserPlus, Handshake, Share2, Shield, MessageSquareText, Eye, Home, Image, MapPin, UserCog, Gift, Phone, Mail, Share, Video, FlaskConical } from "lucide-react";
import Logo from "./Logo";
import flowLogoFull from '../../Gallery/Flow Logo Full.png';

const Sidebar = ({
  openNewCaseModal,
  onNavigate,
  currentView,
  isOpen,
  onClose,
  featureFlags = {},
  unreadChatCount = 0,
  userMeta = {},
  userEmail = "",
  onLogout,
  setIsAccountSettingsOpen,
  isCollapsed = false,
  onToggleCollapse,
  onOpenDialer
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });
  const userMenuRef = useRef(null);
  const sidebarRef = useRef(null);

  const handleMouseEnter = (e, label) => {
    if (!isCollapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const sidebarRect = sidebarRef.current?.getBoundingClientRect();
    setTooltipPosition({ top: rect.top + rect.height / 2 - (sidebarRect?.top || 0) });
    setHoveredItem(label);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get display name
  const getDisplayName = () => {
    if (userMeta.firstName) {
      return [userMeta.firstName, userMeta.lastName].filter(Boolean).join(' ');
    }
    const emailName = (userEmail || userMeta.email || "").split('@')[0];
    return emailName ? emailName.charAt(0).toUpperCase() + emailName.slice(1) : "User";
  };

  const displayName = getDisplayName();
  const initial = displayName[0]?.toUpperCase() || "U";

  // Check if user is RP tier (Starter, Pro, or Ultra)
  const isRpTier = featureFlags.isRpStarter || featureFlags.isRpPro || featureFlags.isRpUltra;

  // Check if user has Ultra/Enterprise tier (for Direct Mail access)
  // Allowed: ultra, enterprise, rp_ultra
  const hasUltraTier = featureFlags.isRpUltra || featureFlags.isUltra || featureFlags.isEnterprise;

  const navItems = [
    // RP Tiers: Flow, Leads, Notes, Teams, Team Chat, Support (+ Realtor Referral for RP_ULTRA)
    { id: "virtual-staging", label: isRpTier ? "Flow" : "Virtual Staging", icon: Home, requiresFeature: "hasVirtualStaging" },
    // Avatar Videos - Ultra/Enterprise only (including rp_ultra)
    { id: "avatar-intro", label: "Avatar Videos", icon: Video, requiresUltra: true },
    { id: "leads", label: "Leads", icon: UserPlus, requiresFeature: "hasLeadsManagement" },
    { id: "notes", label: "Notes", icon: StickyNote, requiresFeature: "hasNotes" },
    { id: "teams", label: "Teams", icon: Users, requiresFeature: "hasTeams" },
    { id: "chat", label: "Team Chat", icon: MessagesSquare, requiresFeature: "hasTeamChat" },
    // Refer & Earn - RP tiers only, earn credits by referring mortgage leads
    { id: "refer-earn", label: "Refer & Earn", icon: Gift, requiresFeature: "hasReferAndEarn" },
    // Realtor Referral - shows for RP_ULTRA and regular Ultra/Enterprise
    { id: "realtor-referral", label: "Realtor Referral", icon: Share2, requiresFeature: "hasRealtorReferral" },
    // Items below are hidden for all RP tiers
    { id: "cases", label: "Cases", icon: Folder, requiresFeature: null, hideForRp: true },
    { id: "lender-dashboard", label: "Dashboard", icon: LayoutDashboard, requiresFeature: null, hideForRp: true },
    { id: "partners", label: "Partners", icon: Handshake, requiresFeature: "hasPartnersManagement", hideForRp: true },
    { id: "insurance-referral", label: "Insurance Referral", icon: Shield, requiresFeature: "hasInsuranceReferral", hideForRp: true },
    { id: "calendar", label: "Calendar", icon: CalendarDays, requiresFeature: "hasCalendar", hideForRp: true },
    { id: "organization", label: "Organization", icon: Building2, requiresFeature: "hasOrganizations", hideForRp: true },
    { id: "sms", label: "SMS Messages", icon: MessageSquareText, requiresFeature: "hasSMS", hideForRp: true },
    { id: "fb-monitor", label: "FB Monitor", icon: Eye, requiresFeature: "hasFBMonitor", hideForRp: true },
    { id: "assistant", label: "Agent Manager", icon: Bot, requiresFeature: "hasCaseAssistant", hideForRp: true },
    { id: "marketing", label: "Marketing", icon: Megaphone, requiresFeature: "hasSocialMedia", hideForRp: true },
    { id: "listings", label: "Listings", icon: MapPin, requiresFeature: "hasListingsManagement", hideForRp: true },
    { id: "ai-images", label: "AI Images", icon: Image, requiresFeature: "hasSocialMedia", hideForRp: true },
    // Direct Mail - Ultra/Enterprise only (including rp_ultra)
    { id: "direct-mail", label: "Direct Mail", icon: Mail, requiresUltra: true },
    // Lab - Ultra/Enterprise only (including rp_ultra). Hidden from Pro/Business.
    { id: "lab", label: "Lab", icon: FlaskConical, requiresUltra: true, href: "/lab", hideForRp: true },
    // Social Scheduler - Ultra/Enterprise only (Metricool integration)
    { id: "social-scheduler", label: "Social Scheduler", icon: Share, requiresUltra: true },
    { id: "developer", label: "Developer", icon: Key, requiresFeature: "hasApiAccess", hideForRp: true },
    { id: "provisioning", label: "Provisioning", icon: UserCog, requiresFeature: "hasProvisioning", hideForRp: true },
    { id: "support", label: "Support", icon: LifeBuoy, requiresFeature: null }
  ];

  // Filter items based on feature flags and tier restrictions
  let availableItems = navItems.filter(item => {
    // Hide items marked hideForRp for ALL RP tier users
    if (item.hideForRp && isRpTier) return false;
    // Check Ultra/Enterprise tier requirement (for Direct Mail)
    // If requiresUltra is set, ONLY check tier - skip feature flag check
    if (item.requiresUltra) {
      return hasUltraTier;
    }
    // Check feature flag requirement
    if (!item.requiresFeature) return true;
    return featureFlags[item.requiresFeature] === true;
  });

  // For RP tiers, Flow is already at the top of navItems, no reordering needed
  // For non-RP accounts, reorder to put key items first
  if (!isRpTier) {
    // Move Leads to top for non-RP users
    const leadsItem = availableItems.find(item => item.id === "leads");
    if (leadsItem) {
      availableItems = [leadsItem, ...availableItems.filter(item => item.id !== "leads")];
    }
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 dark:bg-slate-950/80 bg-slate-900/20 backdrop-blur-sm md:hidden transition-all"
          onClick={onClose}
        />
      )}

      {/* Sidebar Content */}
      <aside
        ref={sidebarRef}
        className={`
          fixed inset-y-0 left-0 z-50 dark:bg-slate-900 bg-white border-r dark:border-slate-800 border-slate-200 flex flex-col transition-all duration-300 ease-in-out
          md:translate-x-0 md:static md:dark:bg-slate-900/50 md:bg-white/50
          ${isOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full md:translate-x-0"}
          ${isCollapsed ? "w-16" : "w-64"}
        `}
      >
        {/* Header with Logo and Collapse Toggle */}
        <div className={`p-4 flex items-center ${isCollapsed ? "justify-center" : "justify-between"}`}>
          {!isCollapsed && (
            <div className="bg-slate-900/90 dark:bg-transparent p-2 dark:p-0 rounded-lg backdrop-blur-sm shadow-sm dark:shadow-none">
              {isRpTier ? (
                <button
                  type="button"
                  onClick={() => (window.location.href = "/")}
                  className="flex items-center focus:outline-none"
                  aria-label="Flow Home"
                >
                  <img
                    src={flowLogoFull}
                    alt="Flow - AI Virtual Staging"
                    className="h-14 w-auto max-w-[12rem] object-contain"
                  />
                </button>
              ) : (
                <Logo className="h-10 w-auto max-w-[8rem]" />
              )}
            </div>
          )}

          {/* Collapse/Expand Toggle - Desktop only */}
          <button
            onClick={onToggleCollapse}
            onMouseEnter={(e) => handleMouseEnter(e, isCollapsed ? "Expand sidebar" : "Collapse sidebar")}
            onMouseLeave={handleMouseLeave}
            className="hidden md:flex items-center justify-center w-8 h-8 rounded-lg dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white dark:hover:bg-slate-800 hover:bg-slate-100 transition-colors"
          >
            {isCollapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>

          {/* Mobile Close Button */}
          <button
            onClick={onClose}
            aria-label="close"
            className="md:hidden dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* New Case Button - Hidden for all RP tier users */}
        {!isRpTier && (
          <div className={`px-2 mb-4 ${isCollapsed ? "px-2" : "px-4"} relative`}>
            <button
              onClick={() => {
                openNewCaseModal();
                onClose?.();
              }}
              onMouseEnter={(e) => handleMouseEnter(e, "New case")}
              onMouseLeave={handleMouseLeave}
              className={`py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors shadow-lg shadow-sky-900/20 ${isCollapsed ? "w-10 h-10 mx-auto px-0" : "w-full px-4"}`}
            >
              <Plus className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span>New case</span>}
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 min-h-0 relative">
          <div className="h-full px-2 space-y-1 overflow-y-auto">
            {!isCollapsed && (
              <div className="px-3 py-2 text-xs font-semibold dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                Workspace
              </div>
            )}
            {availableItems.map((item) => (
              <div key={item.id} className="relative">
                <a
                  href={item.href || "#"}
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.href) {
                      window.location.href = item.href;
                    } else {
                      onNavigate(item.id);
                    }
                    onClose?.();
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, item.label)}
                  onMouseLeave={handleMouseLeave}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg group transition-colors ${isCollapsed ? "justify-center" : ""} ${currentView === item.id
                    ? "dark:text-slate-300 text-sky-700 dark:bg-slate-800 bg-sky-50"
                    : "dark:text-slate-400 text-slate-600 dark:hover:bg-slate-800/50 hover:bg-slate-100/80"
                    }`}
                >
                  <div className="relative">
                    <item.icon className={`w-5 h-5 transition-colors flex-shrink-0 ${currentView === item.id ? "text-sky-600 dark:text-sky-400" : "text-sky-500/70 dark:text-sky-400/70 group-hover:text-sky-600 dark:group-hover:text-sky-400"}`} />
                    {item.id === "chat" && unreadChatCount > 0 && isCollapsed && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white">
                        {unreadChatCount > 99 ? '99+' : unreadChatCount}
                      </span>
                    )}
                  </div>
                  {!isCollapsed && (
                    <>
                      <span className="font-medium flex-1">{item.label}</span>
                      {item.id === "chat" && unreadChatCount > 0 && (
                        <span className="min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
                          {unreadChatCount > 99 ? '99+' : unreadChatCount}
                        </span>
                      )}
                    </>
                  )}
                </a>
              </div>
            ))}
          </div>
        </nav>

        {/* Tooltip rendered outside overflow container */}
        {isCollapsed && hoveredItem && (
          <div
            className="absolute left-full ml-2 px-3 py-1.5 bg-slate-900 dark:bg-slate-700 text-white text-sm font-medium rounded-lg whitespace-nowrap pointer-events-none z-[200] shadow-lg transition-opacity duration-150"
            style={{ top: tooltipPosition.top, transform: 'translateY(-50%)' }}
          >
            {hoveredItem}
          </div>
        )}

        {/* Phone Dialer Button - Quick access to make calls */}
        {onOpenDialer && featureFlags.hasPhoneCalling && (
          <div className={`px-2 mb-2 ${isCollapsed ? "px-2" : "px-4"}`}>
            <button
              onClick={() => {
                onOpenDialer();
                onClose?.();
              }}
              onMouseEnter={(e) => handleMouseEnter(e, "Phone Dialer")}
              onMouseLeave={handleMouseLeave}
              className={`py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-900/30 hover:shadow-green-800/40 ${isCollapsed ? "w-10 h-10 mx-auto px-0" : "w-full px-4"}`}
            >
              <Phone className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span>Phone Dialer</span>}
            </button>
          </div>
        )}

        {/* User Profile Section - Bottom of Sidebar */}
        <div className="border-t dark:border-slate-800 border-slate-200 p-2" ref={userMenuRef}>
          <div className="relative group/user">
            {/* User Menu Popup */}
            {isUserMenuOpen && (
              <div className={`absolute bottom-full mb-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-xl shadow-xl overflow-hidden z-50 ${isCollapsed ? "left-0 w-48" : "left-0 right-0"}`}>
                <div className="px-4 py-3 border-b dark:border-slate-700 border-slate-200">
                  <p className="text-sm font-semibold dark:text-white text-slate-900 truncate">
                    {displayName}
                  </p>
                  <p className="text-xs dark:text-slate-400 text-slate-500 truncate">
                    {userEmail || userMeta.email}
                  </p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Coins className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-xs font-medium dark:text-amber-400 text-amber-600">
                      {(userMeta.credit ?? 0).toLocaleString()} credits
                    </span>
                  </div>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setIsAccountSettingsOpen?.(true);
                      onClose?.();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm dark:text-slate-300 text-slate-700 dark:hover:bg-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      onLogout?.();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm dark:text-slate-300 text-slate-700 dark:hover:bg-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Log out
                  </button>
                </div>
              </div>
            )}

            {/* User Button */}
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              onMouseEnter={(e) => !isUserMenuOpen && handleMouseEnter(e, displayName)}
              onMouseLeave={handleMouseLeave}
              className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg dark:hover:bg-slate-800 hover:bg-slate-100 transition-colors ${isCollapsed ? "justify-center" : ""}`}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-xs font-bold text-white shadow-sm flex-shrink-0">
                {initial}
              </div>
              {!isCollapsed && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium dark:text-white text-slate-900 truncate">
                      {displayName}
                    </p>
                    <p className="text-xs dark:text-slate-400 text-slate-500 truncate">
                      {userMeta.membership || "Free"}
                    </p>
                  </div>
                  <ChevronUp className={`w-4 h-4 dark:text-slate-400 text-slate-500 transition-transform flex-shrink-0 ${isUserMenuOpen ? "" : "rotate-180"}`} />
                </>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
